// Helpers shared by the content/ops admin pages (Content, Releases, Games, Payments, Diagnostics, Audit, Settings).
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useApi } from "../../api.ts";
import { fmtDateTime } from "../../format.ts";
import { Button, Card, EmptyState, IconButton, Loading, LoadError, Pagination, SearchInput, Table, THead, TBody, Th, useDebounced, type IconName } from "../../ui/index.tsx";
import type { Paged, Severity } from "../../../shared/types.ts";

export const severityTone = (s: Severity) => (s === "error" ? "bad" : s === "warning" ? "warn" : "info");

/** Right-aligned button row at the bottom of a dialog form. */
export function FormActions({ onCancel, submitLabel, loading, disabled, icon }: {
  onCancel: () => void; submitLabel: string; loading?: boolean; disabled?: boolean; icon?: IconName;
}) {
  return (
    <div className="mt-5 flex justify-end gap-2 border-t border-line pt-4">
      <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      <Button variant="primary" type="submit" icon={icon} loading={loading} disabled={disabled}>{submitLabel}</Button>
    </div>
  );
}

/** Username that links to the admin user page when the id is known. */
export function UserCell({ id, name }: { id: string | null; name: string | null }) {
  if (!name) return <span className="text-faint">{id ? "deleted user" : "—"}</span>;
  if (!id) return <span>{name}</span>;
  return <Link to={`/admin/users/${id}`} className="font-medium hover:text-accent hover:underline">{name}</Link>;
}

export const When = ({ at }: { at: string }) => <span className="whitespace-nowrap text-muted tabular-nums">{fmtDateTime(at)}</span>;

/** Server-paged list with a debounced search box; `renderRow` returns one or more <Tr>. */
export function PagedPanel<T>({ base, limit = 25, searchPlaceholder, emptyTitle, emptyIcon = "info", columns, renderRow }: {
  base: string;
  limit?: number;
  searchPlaceholder: string;
  emptyTitle: string;
  emptyIcon?: IconName;
  columns: ReactNode[];
  renderRow: (item: T) => ReactNode;
}) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const dq = useDebounced(q.trim());
  useEffect(() => setPage(1), [dq]);

  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (dq) params.set("q", dq);
  const { data, error, loading, reload } = useApi<Paged<T>>(`${base}?${params}`);

  return (
    <Card padded={false}>
      <div className="flex items-center gap-2 border-b border-line p-3">
        <SearchInput value={q} onChange={setQ} placeholder={searchPlaceholder} className="w-full max-w-sm" />
        <IconButton icon="refresh" label="Refresh" onClick={() => void reload()} disabled={loading} />
      </div>
      {!data ? (
        loading ? <Loading /> : <LoadError error={error} retry={() => void reload()} />
      ) : data.items.length === 0 ? (
        <EmptyState icon={emptyIcon} title={dq ? "No matches" : emptyTitle} body={dq ? `Nothing found for "${dq}".` : undefined} />
      ) : (
        <>
          <Table>
            <THead><tr>{columns.map((c, i) => <Th key={i}>{c}</Th>)}</tr></THead>
            <TBody>{data.items.map(renderRow)}</TBody>
          </Table>
          <Pagination page={data.page} pages={data.pages} total={data.total} onChange={setPage} />
        </>
      )}
    </Card>
  );
}
